/* Hook for checking feature access based on the user's subscription tier.
   Returns tier info, usage data, and a canUse() function for gating. */

import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import {
  type SubscriptionTier,
  type FeatureId,
  FEATURE_GATES,
  hasAccess,
} from "@/lib/featureGates";

export interface CanUseResult {
  allowed: boolean;
  reason?: "no_tier" | "limit_reached";
  remaining?: number;
  limit?: number;
  requiredTier: SubscriptionTier;
}

export function useFeatureGate() {
  const tierData = useQuery(api.billing.getUserTier);

  const isLoading = tierData === undefined;
  const tier: SubscriptionTier = tierData?.tier ?? "free";
  const usage: Record<string, number> = tierData?.usage ?? {};

  const isFree = tier === "free";
  const isPro = tier === "pro" || tier === "pro_ai";
  const isProAi = tier === "pro_ai";

  function canUse(featureId: FeatureId): CanUseResult {
    const gate = FEATURE_GATES[featureId];
    const requiredTier = gate.requiredTier;

    // User has full access to this tier
    if (hasAccess(tier, requiredTier)) {
      return { allowed: true, requiredTier };
    }

    // Check if there's a free-tier limit
    const limit = tier === "free" ? gate.freeLimit : gate.proLimit;
    if (limit !== undefined) {
      const used = usage[featureId] ?? 0;
      const remaining = Math.max(0, limit - used);
      if (remaining > 0) {
        return { allowed: true, remaining, limit, requiredTier };
      }
      return { allowed: false, reason: "limit_reached", remaining: 0, limit, requiredTier };
    }

    // No limit — feature simply requires a higher tier
    return { allowed: false, reason: "no_tier", requiredTier };
  }

  return {
    tier,
    isFree,
    isPro,
    isProAi,
    usage,
    canUse,
    isLoading,
  };
}
